import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AppShell } from "@/components/layouts/AppShell";
import { useAuth } from "@/lib/auth";
import { usePersona } from "@/lib/persona";

export const Route = createFileRoute("/")({
  component: LandingPage,
});

/**
 * Persona-aware public landing.
 * If the user is already authenticated AND has the right role for this
 * subdomain, bounce straight to /app — that's the home screen behavior we
 * want when the PWA is launched from the OS launcher with a persisted session.
 */
function LandingPage() {
  const persona = usePersona();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (user && user.groups.includes(persona.role)) {
      void navigate({ to: "/app", replace: true });
    }
  }, [loading, user, persona.role, navigate]);

  return (
    <AppShell>
      <section className="container py-16 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="text-xs uppercase tracking-widest text-gold">Food &amp; Wine Festival</div>
          <h1 className="mt-3 font-serif text-5xl text-palm md:text-6xl">{persona.brand.name}</h1>
          <p className="mt-6 text-balance text-lg text-muted-foreground">{persona.brand.tagline}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="accent">
              <Link to="/login">Sign in</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="https://tasteoftulum.org">Festival home</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container pb-20">
        <div className="grid gap-4 md:grid-cols-3">
          {persona.role === "attendee" && (
            <>
              <FeatureCard
                title="Browse the lineup"
                description="Restaurants, hotels, artists, wine dinners — all month long."
              />
              <FeatureCard
                title="Buy tickets"
                description="Stripe-backed checkout. Apple Pay / Google Pay ready."
              />
              <FeatureCard
                title="Carry them offline"
                description="Tickets cache to your device. Gates scan even without signal."
              />
            </>
          )}
          {persona.role === "partner" && (
            <>
              <FeatureCard
                title="Manage your listing"
                description="Edit your profile, swap photos, add experiences."
              />
              <FeatureCard
                title="See real-time orders"
                description="Who bought what, when. Export to CSV."
              />
              <FeatureCard
                title="Get paid"
                description="Stripe-backed payouts on the cadence you set."
              />
            </>
          )}
          {persona.role === "organizer" && (
            <>
              <FeatureCard
                title="Tenant control"
                description="Approve, suspend, edit partners. Full audit trail."
              />
              <FeatureCard
                title="Schedule + finance"
                description="Calendar of events. Ledger of every dollar."
              />
              <FeatureCard
                title="Audit + compliance"
                description="Append-only log. Every write tied to a Cognito sub."
              />
            </>
          )}
        </div>
      </section>
    </AppShell>
  );
}

function FeatureCard({ title, description }: { title: string; description: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent />
    </Card>
  );
}
